import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormsDataService } from './forms-data.service';
import { FormlyInputType, FormlyType, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import {
  ActionButtonClass,
  CallContextType,
  EVENT_CANCEL_ACTION,
  EVENT_DELETE_ACTION,
  EVENT_INIT_ACTION,
  EVENT_LOAD_ACTION,
  EVENT_ON_CHANGE,
  EVENT_REQUEST_ITEMS,
  FIELD_TYPE_EMAIL,
  FIELD_TYPE_URL,
  FieldStatus,
  FormsService,
  IGA3CallContext,
  IGAFormAction,
  IGAFormActionEventRequest,
  IGAFormEvent,
  IGAFormField,
  IGAFormFieldChange,
  IGAFormItem,
  IGAFormMetadata,
  IGAFormSection,
  IGAFormTypeValue,
  ResponseType,
  TYPE_DELETE,
  TYPE_UPDATE
} from './forms.service';
import _ from 'underscore';
import { GaMessageService, MessageType } from '@app/shared/message/ga-message.service';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { Subscription } from 'rxjs';
import { IObject } from '@type_definitions/datatypes';
import { GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { EVENT_FORMLY_VALUE_INIT } from '@app/shared/formly/ga-formly.util';
import { util } from '@app/shared/util';
import { Dictionary } from '@type_definitions/vendor';
import { TranslationService } from '@app/shared/translation/translation.service';
import { BaseCustomComponent } from './base-custom.component';
import { IGA3ReportAction } from '@app/shared/report/actions/report-actions-config.service';

@Component({
  selector: 'ga-forms',
  template: `
    <div [class.modal-body]="isInDialog" *ngIf="formBusy">
      <h2 translate>
        <activity-indicator [useDefaultText]="true"></activity-indicator>
      </h2>
    </div>
    
    <div [class.col-space-top]="!isInDialog" [hidden]="formBusy">
      <div [class.modal-header]="isInDialog" [hidden]="isError">
        <h2 [ngClass]="{'form-header': !isInDialog, 'modal-title': isInDialog}">{{title || metadata?.title}} <span class="fa fa-info-circle info-icon info-header" *ngIf="metadata?.help" (click)="toggleFormHelp()"></span></h2>
      </div>

      <div [class.modal-body]="isInDialog">
        <div class="row" *ngIf="isError">
          <div class="col-sm-12 col-space-top">
            <div *ngIf="isInDialog" class="float-end">
              <button type="button" class="btn-close" (click)="cancel()">
                <span class="visually-hidden" translate>GA.BUTTONS.CLOSE</span>
              </button>
            </div>
            <span class="clearfix"></span>
            <div class="alert alert-danger"><strong>Can't access this form</strong></div>
          </div>
        </div>

        <forms-help [help]="metadata?.help"></forms-help>
        
        <grid [isInDialog]="isInDialog">
          <ng-container *ngFor="let section of metadata?.sections; let i = index; trackById">
            <forms-container [form]="form" [fields]="fields[section.id] || []" [model]="model" [collapsible]="section.style?.collapsible" [sectionTitle]="section.title" [sectionHelp]="section.help" [sectionId]="section.id" *gridCol></forms-container>
          </ng-container>
        </grid>
      </div>

      <div [ngClass]="{'col-space-top float-end': !isInDialog, 'modal-footer': isInDialog }" [hidden]="isError">
        <button class="btn col-margin-left ga3-action" *ngFor="let actionButton of actionButtons; trackById" 
          [ngClass]="getActionButtonClass(actionButton.id)" (click)="doAction(actionButton)" [busyButton]="actionBusy[actionButton.id]" [disabled]="actionDisabled[actionButton.id]" [id]="actionButton.id">{{actionButton.caption}}</button>
      </div>
    </div>
  `,
  styleUrls: ['./ga-forms.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FormsComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() public formId:string;
  @Input() public reportId: number;
  @Input() public key:string | string[] | number[];
  @Input() public isInDialog:boolean;
  @Input() public context:Dictionary<IGAFormTypeValue>;
  @Input() public reportActionConfig: IGA3ReportAction;
  // this will use context as key rather than key input
  // this is usually for composite keys form
  @Input() public compositeKeyForm: boolean;
  @Input() public title: string;
  @Input() public showPostActionMessage: boolean = true;

  @Output() public onSaving:EventEmitter<boolean> = new EventEmitter();
  @Output() public onSaved:EventEmitter<IObject> = new EventEmitter();
  @Output() public onError:EventEmitter<void> = new EventEmitter();
  @Output() public onDialogCancel:EventEmitter<void> = new EventEmitter();

  public metadata:IGAFormMetadata;
  public fields:{ [ key:string ]:IExtraFieldConfigurationObject[] } = {};
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:Dictionary<IGAFormItem> = {};
  public formBusy:boolean;
  public isError:boolean;
  public actionBusy:{ [ key:string ]:boolean } = {};
  public actionDisabled:{ [ key:string ]:boolean } = {};
  public actionButtons: IGAFormAction[];

  private fieldsOnChangeSubscription:Subscription;
  private gaEventSubscription:Subscription;
  private customBindings: BaseCustomComponent;
  private callContext: IGA3CallContext;

  constructor(
    private formsDataService:FormsDataService,
    private formsService:FormsService,
    private messageService:GaMessageService,
    private validateService:GaValidateService,
    private gaEventService:GaEventService,
    private changeDetectorRef: ChangeDetectorRef,
    private translationService: TranslationService
  ) {
    //
  }

  public ngOnInit():void {
    // reset model and form for rendering clean form
    this.form.reset();
    this.model = {};
    this.formBusy = true;

    let postActionEvent: Promise<IGAFormMetadata | string>;
    this.callContext = {
      type: CallContextType.REPORT,
      reportId: this.reportId,
      selectedKeys: undefined
    };

    if (this.reportActionConfig) {
      this.callContext.selectedKeys = <string[]>this.key;
      postActionEvent = this.formsDataService.postActionEvent(this.formId, this.formsService.initReportActionRequest(this.reportActionConfig.init, this.context, this.callContext));
    } else {
      postActionEvent = this.formsDataService.postActionEvent(this.formId, this.formsService.initEventRequest(<string>this.key, this.context, this.compositeKeyForm, this.callContext));
    }

    postActionEvent
      .then(this.processMetadata)
      .catch((error:string) => {
        // prevent showing form when its error
        this.isError = true;
        this.displayErrorMessage(error);

        // when init ga3 form and there is an error we don't show dialog
        if (this.isInDialog) {
          this.cancel();
        }
      })
      .finally(() => {
        this.formBusy = false;
      });

    // listen on fields change
    this.listen();
  }

  public ngOnDestroy():void {
    this.formsDataService.cancelAllRequests(this.formId);
    this.formsDataService.clearFormCookies(this.formId);
    this.fieldsOnChangeSubscription.unsubscribe();
    this.gaEventSubscription.unsubscribe();
    this.formsService.clearSubject();
  }

  public ngAfterViewChecked(): void {
    // force change detection because form control attributes value might change after initial detect change
    // https://indepth.dev/posts/1001/everything-you-need-to-know-about-the-expressionchangedafterithasbeencheckederror-error
    this.changeDetectorRef.detectChanges();
  }

  private listen():void {
    // listen on field metadata change
    this.fieldsOnChangeSubscription = this.formsService.fieldsOnChange.subscribe(this.onFieldChange);

    this.gaEventSubscription = this.gaEventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_FORMLY_VALUE_INIT) {
        if (util.isNullOrUndefinedOrEmpty(this.model[ event.data.key ])) {
          this.model[ event.data.key ] = event.data.value;
        }
      }
    });
  }

  private displayErrorMessage = (msg:string):void => {
    if (msg) {
      // display message
      this.messageService.showFeedbackMessage(msg, MessageType.DANGER);
    }

    if (this.onError) {
      // emit error
      this.onError.emit();
    }
  };

  private fillField(field:IExtraFieldConfigurationObject, formField:IGAFormField, reload?:boolean):void {
    field.templateOptions.loading = true;
    field.templateOptions.disabled = true;

    const eventRequest:IGAFormActionEventRequest = this.formsService.createEventRequest(this.model, this.metadata.sections, formField, undefined, this.callContext);

    // mark as loading for target field
    _.forEach(this.metadata.sections, (section:IGAFormSection) => {
      _.forEach(section.fields, (sectionField:IGAFormField) => {
        sectionField.status = sectionField.id === formField.id? (reload? undefined : FieldStatus.LOADING) : sectionField.status;
        return sectionField;
      });
    });

    this.formsDataService.postActionEvent(this.formId, eventRequest)
      .then((metadata:IGAFormMetadata) => this.formsService.updateMetadata(this.metadata, metadata))
      .catch(this.displayErrorMessage)
      .finally(() => {
        field.templateOptions.loading = false;
        field.templateOptions.disabled = false;
      });
  }

  private processMetadata = (metadata:IGAFormMetadata):void => {
    if (metadata && metadata.type === ResponseType.FORM) {
      this.metadata = this.formsService.mergeMetadata(this.metadata, metadata);
      // save action buttons
      this.actionButtons = _.clone(this.metadata.actions);

      // create sections
      this.createSections(this.metadata?.sections);
    }
  };

  private onFieldChange = (sectionField:IGAFormFieldChange):void => {
    // if there is section needed to update then update the formly field configuration
    if (sectionField) {

      // event type of update includes adding / updating field
      if (sectionField.event === TYPE_UPDATE) {
        // get current field index from formly fields
        const fieldIndex = _.findIndex(this.fields[ sectionField.sectionId ], { key: sectionField.id });

        // if section field is exist in current rendered field else add new field/section
        if (fieldIndex > -1) {
          // update formly config based on updatable field metadata
          if (sectionField.items) {
            this.fields[ sectionField.sectionId ][ fieldIndex ].templateOptions.options = sectionField.items;
          }

          // if section field is readonly then change field type to value
          if (this.formsService.isFieldReadonly(sectionField)) {
            this.fields[ sectionField.sectionId ][ fieldIndex ].type = FormlyType.VALUE;
          }

          // update model based on field type
          if (!this.formsService.isValueEmpty(sectionField.type, sectionField.data)) {
            this.form.patchValue({ [ sectionField.id ]: this.formsService.extractModelValue(sectionField.type, sectionField.data, sectionField.visibility) });
          }

          // update form validation
          if (sectionField.validation) {
            this.updateFieldValidation(this.fields[ sectionField.sectionId ][ fieldIndex ], sectionField);
          }
        } else {
          // add new container and field
          this.fields[ sectionField.sectionId ] = this.fields[ sectionField.sectionId ]?.length? this.fields[ sectionField.sectionId ].concat(
            this.createSectionFields([ sectionField ])
          ) : this.createSectionFields([ sectionField ]);
        }
      }
      // event type of delete is removing field from sections
      else if (sectionField.event === TYPE_DELETE) {
        this.fields[ sectionField.sectionId ] = _.reject(this.fields[ sectionField.sectionId ], (s:IExtraFieldConfigurationObject) => s.key === sectionField.id);
      }
    }
  };

  private createSectionFields(fields:IGAFormField[]):IExtraFieldConfigurationObject[] {
    _.each(fields, (field:IGAFormField) => {
      // set model before building the forms
      // using model instead of formControl because form will be rebuild after creating section fields finished
      if (field.data && !_.isEmpty(field.data)) {
        this.model[field.id] = this.formsService.extractModelValue(field.type, field.data, field.visibility);
      }
    });

    const preparedFields:IExtraFieldConfigurationObject[] = _.map(fields, (field: IGAFormField) => {
      // cleanup the form control, to prevent form control overlapped with newly rebuilt form
      this.form.removeControl(field.id);

      this.customBindings = this.getCustomComponentBindings();

      const registeredField:IExtraFieldConfigurationObject = this.formsService.registerField(field, this.customBindings, this.model);
      const filteredEvents:IGAFormEvent[] = _.filter(field.events, (event:IGAFormEvent) => event.type === EVENT_REQUEST_ITEMS && !event.when);

      // register field onChange handler
      this.registerFormlyOnChange(registeredField, field);

      if (registeredField.type === FormlyType.SEARCH) {
        registeredField.templateOptions.searchFunction = {
          request: (filterValue: string): Promise<IGAFormItem[]> => {
            // set field status to unloaded
            this.setFieldStatus(field, FieldStatus.UNLOADED);
            const eventRequest: IGAFormActionEventRequest = this.formsService.createEventRequest(this.model, this.metadata.sections, field, filterValue, this.callContext);

            return this.formsDataService.postActionEvent(this.formId, eventRequest)
              .then((response: IGAFormMetadata) => {
                let items: IGAFormItem[];

                for (const section of response.sections) {
                  for (const formField of section.fields) {
                    if (formField.id === field.id) {
                      items = formField.items;
                      break;
                    }
                  }
                }

                return items;
              });
          }
        };
      } else {
        // check if field still has unloaded status to fetch list items
        // and field should be editable
        if ((!field.status || field.status === FieldStatus.UNLOADED) && filteredEvents?.length && this.formsService.isFieldEditable(field)) {
          // if field status is undefined then add default of UNLOADED state to invoke request items event
          field.status = field.status || FieldStatus.UNLOADED;
          this.fillField(registeredField, field);
        }
      }

      return registeredField;
    });

    return preparedFields;
  }

  private createSections(sections:IGAFormSection[]):void {
    _.each(sections, (section:IGAFormSection) => {
      this.fields[section.id] = this.createSectionFields(section.fields);
    });
  }

  private getCustomComponentBindings(): BaseCustomComponent {
    return { form: this.form, fields: this.fields, metadata: this.metadata };
  }

  private registerFormlyOnChange = (registeredField:IExtraFieldConfigurationObject, field:IGAFormField):void => {
    registeredField.templateOptions.onChange = (): void => {
      // flatten form fields
      const formFields:IGAFormField[] = _.flatten(
        _.map(this.metadata.sections, (section:IGAFormSection) => {
          return _.map(section.fields, (sectionField:IGAFormField) => sectionField);
        })
      );

      // flatten registered formly fields
      const formlyFields:IExtraFieldConfigurationObject[] = _.flatten(
        _.map(this.fields, (fields:IExtraFieldConfigurationObject[]) => fields)
      );

      _.each(field.events, (event:IGAFormEvent) => {
        // if there are on change event in field events and its type is REQUEST_ITEMS
        if (event.when === EVENT_ON_CHANGE && event.type === EVENT_REQUEST_ITEMS) {
          this.form.controls[ event.field ].reset();

          const targetField = _.findWhere(formFields, { id: event.field });

          // for the initial change target field will not have any state
          if (targetField && (!targetField.status || targetField.status === FieldStatus.LOADED)) {
            targetField.status = FieldStatus.UNLOADED;
            this.fillField(_.findWhere(formlyFields, { key: event.field }), targetField, true);
          }
        }
      });
    };
  };

  private updateFieldValidation(field:IExtraFieldConfigurationObject, sectionField:IGAFormField):void {
    field.templateOptions.required = !sectionField.validation.optional;
    field.templateOptions.minLength = sectionField.validation.minLength;
    field.templateOptions.maxLength = sectionField.validation.maxLength;
    field.templateOptions.min = sectionField.validation.min;
    field.templateOptions.max = sectionField.validation.max;

    if (sectionField.validation.pattern && sectionField.validation.pattern === FIELD_TYPE_EMAIL || sectionField.validation.pattern === FIELD_TYPE_URL) {
      field.templateOptions.type = sectionField.validation.pattern === FIELD_TYPE_EMAIL? FormlyInputType.EMAIL : FormlyInputType.URL;
    } else {
      field.templateOptions.pattern = sectionField.validation.pattern;
    }

    // add timeout to let 'formControl' fully rendered
    setTimeout(() => {
      field.formControl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  public getActionButtonClass(id:string):string {
    return ActionButtonClass[id] ? ActionButtonClass[id] : ActionButtonClass['SAVE-ACTION'];
  }

  public cancel():void {
    this.onDialogCancel.emit();
  }

  public doAction(action:IGAFormAction):void {
    // if action is cancel then invoke cancel() method instead
    if (action.id === EVENT_CANCEL_ACTION) {
      return this.cancel();
    }

    const INITIAL_ACTION_EVENTS:string[] = [
      EVENT_INIT_ACTION,
      EVENT_LOAD_ACTION
    ];
    const isInitialActionEvent:boolean = _.contains(INITIAL_ACTION_EVENTS, action.id);
    const requestBody:IGAFormActionEventRequest = this.formsService.createActionRequest(this.model, this.metadata.sections, [ action ], undefined, this.callContext);
    let validationPromise: Promise<void | string[]> = Promise.resolve();

    this.setActionBusy(action.id, true);

    // if action is not init and there is invalid fields then do nothing
    if (!isInitialActionEvent) {
      validationPromise = this.validateForm();
    }

    validationPromise
      .then(() => {
        this.onSaving.emit(true);
        this.formsDataService.postActionEvent(this.formId, requestBody)
          .then((metadata:IGAFormMetadata) => {
            if (!isInitialActionEvent) {
              const label: string = this.title || this.metadata?.title;

              if (this.showPostActionMessage) {
                const message: string = action.id === EVENT_DELETE_ACTION ? this.translationService.instant('GA.GENERAL.STATUS_MESSAGE_DELETED', { label })
                  : this.translationService.instant('GA.GENERAL.STATUS_MESSAGE_SAVED', { label });
                this.messageService.showFeedbackMessage(message);
              }

              this.onSaved.emit(this.formsService.getKeyValue(metadata));
            }

            if (action.id === EVENT_DELETE_ACTION) {
              // clear form when delete successful
              this.form.reset();
            }

            // re-initialize form when it's not in dialog
            if (!this.isInDialog) {
              this.processMetadata(metadata);
            }
          })
          .catch(this.displayErrorMessage)
          .finally(() => {
            if (!isInitialActionEvent) {
              this.onSaving.emit(false);
            }
            this.setActionBusy(action.id, false);
          });
      })
      .catch(() => {
        this.setActionBusy(action.id, false);
      });
  }

  // the action that is being executed should show a spinner, the other ones should simply be disabled
  private setActionBusy(id:string, busy:boolean) {
    _.each(this.actionButtons, action => {
      if (id === action.id) {
        this.actionBusy[action.id] = busy;
      } else {
        this.actionDisabled[action.id] = busy;
      }
    });
  }

  private validateForm():Promise<void|string[]> {
    let fields:IExtraFieldConfigurationObject[] = [];

    _.each(this.fields, (field:IExtraFieldConfigurationObject[]) => {
      fields = fields.concat(field);
    });
    return this.validateService.validateFields(fields, this.model, true);
  }

  private setFieldStatus(targetField: IGAFormField, status: FieldStatus): void {
    const metadata: IGAFormMetadata = util.deepClone(this.metadata);

    for (const section of metadata.sections) {
      for (const field of section.fields) {
        if (field.id === targetField.id) {
          field.status = status;
        }
      }
    }

    this.formsService.updateMetadata(this.metadata, metadata);
  }

  public toggleFormHelp(): void {
    this.metadata.help.textVisible = !this.metadata.help.textVisible;
  }
}